import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Search, Download, X, Loader2, MessageCircle, RefreshCw, CheckCircle2
} from 'lucide-react';
import { leadsService, type Lead } from '../../services/leadsService';
import jsPDF from 'jspdf';

export function CrmFees() {
  const [searchParams] = useSearchParams();
  const [students, setStudents] = useState<Lead[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Lead[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('all');
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Lead | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [feeForm, setFeeForm] = useState({
    fee: 0,
    paid1: 0,
    paid2: 0,
    paymentMode: 'UPI / GPay / PhonePe',
    transactionRef: '',
  });

  useEffect(() => {
    loadStudents();
    const queryStudent = searchParams.get('student');
    if (queryStudent) {
      setSearchQuery(queryStudent);
    }
  }, [searchParams]);

  useEffect(() => {
    filterStudents();
  }, [students, searchQuery, paymentStatusFilter]);

  const loadStudents = async () => {
    try {
      const allLeads = await leadsService.listLeads();
      const enrolledStudents = allLeads.filter((lead) => lead.status === 'enrolled' || lead.status === 'registered');
      setStudents(enrolledStudents);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to load student fees' });
    }
  };

  const filterStudents = () => {
    let filtered = [...students];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (student) =>
          student.name.toLowerCase().includes(q) ||
          student.email?.toLowerCase().includes(q) ||
          student.phone.includes(q) ||
          student.courseInterest?.toLowerCase().includes(q)
      );
    }

    if (paymentStatusFilter !== 'all') {
      filtered = filtered.filter((student) => {
        const totalPaid = (student.paid1 || 0) + (student.paid2 || 0);
        const due = student.due || 0;
        if (paymentStatusFilter === 'paid') return due === 0 && totalPaid > 0;
        if (paymentStatusFilter === 'partial') return totalPaid > 0 && due > 0;
        if (paymentStatusFilter === 'pending') return totalPaid === 0;
        return true;
      });
    }

    setFilteredStudents(filtered);
  };

  const handleUpdateFee = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudent) return;

    if (!feeForm.fee || feeForm.fee <= 0) {
      setMessage({ type: 'error', text: 'Total fee amount must be greater than 0' });
      return;
    }

    setActionLoading(true);
    try {
      const updated = await leadsService.updateFeeStatus(
        selectedStudent.leadId,
        feeForm.fee,
        feeForm.paid1,
        feeForm.paid2.toString()
      );
      setStudents(students.map((s) => (s.leadId === updated.leadId ? updated : s)));
      setMessage({ type: 'success', text: 'Fee status & payments recorded successfully' });
      setShowUpdateModal(false);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to update fee' });
    } finally {
      setActionLoading(false);
    }
  };

  // Generate Official CloudBinary PDF Receipt
  const generatePDFReceipt = (student: Lead) => {
    const doc = new jsPDF();
    const receiptNo = `CB-REC-${Date.now().toString().slice(-6)}`;
    const dateStr = new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
    const totalPaid = (student.paid1 || 0) + (student.paid2 || 0);
    const balanceDue = student.due || 0;

    // Header Background
    doc.setFillColor(15, 23, 42); // slate-900
    doc.rect(0, 0, 210, 40, 'F');

    // Header Text
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('CLOUDBINARY ACADEMY', 15, 20);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(148, 163, 184); // slate-400
    doc.text('Forward Deployed Engineer (FDE) & Cloud Computing Programs', 15, 27);
    doc.text('Admissions Desk: +91 9100073006 | info@cloudbinary.in | lms.cloudbinary.in', 15, 33);

    // Title & Receipt No
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('OFFICIAL FEE PAYMENT RECEIPT', 15, 55);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(`Receipt No: ${receiptNo}`, 140, 55);
    doc.text(`Date: ${dateStr}`, 140, 62);

    // Divider
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.line(15, 68, 195, 68);

    // Student Info Block
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text('STUDENT ADMISSION DETAILS', 15, 78);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(51, 65, 85);
    doc.text(`Student Name: ${student.name}`, 15, 87);
    doc.text(`Contact Phone: ${student.phone}`, 15, 94);
    doc.text(`Email: ${student.email || 'N/A'}`, 15, 101);
    doc.text(`Enrolled Program: ${student.courseInterest || 'Forward Deployed Engineer (FDE)'}`, 15, 108);

    // Table Header
    doc.setFillColor(241, 245, 249);
    doc.rect(15, 120, 180, 10, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('Description', 20, 127);
    doc.text('Amount (INR)', 150, 127);

    // Table Rows
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Agreed Course Fee`, 20, 140);
    doc.text(`Rs. ${(student.fee || 0).toLocaleString('en-IN')}`, 150, 140);

    doc.text(`Installment 1 (Paid)`, 20, 150);
    doc.text(`Rs. ${(student.paid1 || 0).toLocaleString('en-IN')}`, 150, 150);

    doc.text(`Installment 2 (Paid)`, 20, 160);
    doc.text(`Rs. ${(student.paid2 || 0).toLocaleString('en-IN')}`, 150, 160);

    doc.line(15, 168, 195, 168);

    // Summary Totals
    doc.setFont('helvetica', 'bold');
    doc.text(`Total Amount Paid:`, 100, 178);
    doc.setTextColor(16, 185, 129); // emerald
    doc.text(`Rs. ${totalPaid.toLocaleString('en-IN')}`, 150, 178);

    doc.setTextColor(15, 23, 42);
    doc.text(`Outstanding Due Balance:`, 100, 188);
    doc.setTextColor(245, 158, 11); // amber
    doc.text(`Rs. ${balanceDue.toLocaleString('en-IN')}`, 150, 188);

    // Footer & Signature
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text('This is a computer-generated fee receipt and requires no physical signature.', 15, 220);
    doc.text('Payments once made are non-refundable as per CloudBinary admissions policy.', 15, 226);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text('Authorized Admissions Officer', 140, 240);
    doc.text('CloudBinary Academy', 140, 246);

    doc.save(`CloudBinary_FeeReceipt_${student.name.replace(/\s+/g, '_')}_${receiptNo}.pdf`);
    setMessage({ type: 'success', text: `Receipt downloaded for ${student.name}` });
  };

  const totalFeeRevenue = students.reduce((acc, s) => acc + (s.fee || 0), 0);
  const totalPaidRevenue = students.reduce((acc, s) => acc + (s.paid1 || 0) + (s.paid2 || 0), 0);
  const totalPendingDue = students.reduce((acc, s) => acc + (s.due || 0), 0);

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Toast message */}
      {message && (
        <div className={`p-4 rounded-xl text-xs font-semibold flex items-center justify-between shadow-lg ${
          message.type === 'success' ? 'bg-emerald-950/90 text-emerald-300 border border-emerald-800/80' : 'bg-rose-950/90 text-rose-300 border border-rose-800/80'
        }`}>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <span>{message.text}</span>
          </div>
          <button onClick={() => setMessage(null)} className="text-slate-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-slate-900/90 border border-slate-800/80 p-5 rounded-2xl shadow-lg">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-extrabold text-white tracking-tight font-heading">
              Student Fees & Payment Receipts
            </h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-500/20 text-blue-300 border border-blue-500/30">
              ₹{totalPaidRevenue.toLocaleString('en-IN')} Collected
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Track student fee installments, balances, payment modes, and generate official PDF receipts.
          </p>
        </div>

        <button
          onClick={loadStudents}
          className="flex items-center gap-1.5 px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-semibold border border-slate-700 transition-all"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh Ledger
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Total Agreed Contract Fees</span>
          <div className="text-2xl font-black text-white font-heading mt-2">
            ₹{totalFeeRevenue.toLocaleString('en-IN')}
          </div>
          <p className="text-[11px] text-slate-400 mt-1">{students.length} Enrolled Contracts</p>
        </div>

        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Total Collected Amount</span>
          <div className="text-2xl font-black text-emerald-400 font-heading mt-2">
            ₹{totalPaidRevenue.toLocaleString('en-IN')}
          </div>
          <p className="text-[11px] text-slate-400 mt-1">
            {totalFeeRevenue > 0 ? Math.round((totalPaidRevenue / totalFeeRevenue) * 100) : 0}% Realized
          </p>
        </div>

        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Outstanding Due Balance</span>
          <div className="text-2xl font-black text-amber-400 font-heading mt-2">
            ₹{totalPendingDue.toLocaleString('en-IN')}
          </div>
          <p className="text-[11px] text-slate-400 mt-1">Installment 2 Follow-ups Pending</p>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="flex flex-col sm:flex-row items-center gap-3 bg-slate-900/60 border border-slate-800/80 p-3 rounded-xl">
        <div className="relative flex-1 w-full">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by student name, phone (+91), email, or course..."
            className="w-full pl-10 pr-4 py-2 bg-slate-950 border border-slate-800 rounded-lg text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-indigo-500"
          />
        </div>

        <select
          value={paymentStatusFilter}
          onChange={(e) => setPaymentStatusFilter(e.target.value)}
          className="px-3 py-2 bg-slate-950 border border-slate-800 rounded-lg text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
        >
          <option value="all">All Payment Statuses</option>
          <option value="paid">Fully Paid (Zero Due)</option>
          <option value="partial">Partial Paid (Due Remaining)</option>
          <option value="pending">Pending Payment</option>
        </select>
      </div>

      {/* Fees Table */}
      <div className="bg-slate-900/80 border border-slate-800/90 rounded-2xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-300">
            <thead className="bg-slate-950 text-slate-400 uppercase tracking-wider text-[10px] border-b border-slate-800">
              <tr>
                <th className="p-4">Student</th>
                <th className="p-4">Program</th>
                <th className="p-4">Agreed Fee</th>
                <th className="p-4">Inst. 1</th>
                <th className="p-4">Inst. 2</th>
                <th className="p-4">Total Paid</th>
                <th className="p-4">Due Balance</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Receipt & Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60 font-medium">
              {filteredStudents.length === 0 ? (
                <tr>
                  <td colSpan={9} className="p-8 text-center text-slate-500 text-xs">
                    No student fee records found matching your filters.
                  </td>
                </tr>
              ) : (
                filteredStudents.map((student) => {
                  const totalPaid = (student.paid1 || 0) + (student.paid2 || 0);
                  const isFullyPaid = (student.due || 0) === 0 && (student.fee || 0) > 0;

                  return (
                    <tr key={student.leadId} className="hover:bg-slate-800/40 transition-colors">
                      <td className="p-4">
                        <div className="font-bold text-white text-sm">{student.name}</div>
                        <div className="text-[11px] text-slate-400 font-mono">{student.phone}</div>
                      </td>
                      <td className="p-4">
                        <span className="text-slate-200 font-semibold">{student.courseInterest || 'FDE Program'}</span>
                      </td>
                      <td className="p-4 font-mono font-bold text-white">
                        ₹{(student.fee || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="p-4 font-mono text-slate-300">
                        ₹{(student.paid1 || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="p-4 font-mono text-slate-300">
                        ₹{(student.paid2 || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="p-4 font-mono font-bold text-emerald-400">
                        ₹{totalPaid.toLocaleString('en-IN')}
                      </td>
                      <td className="p-4 font-mono font-bold text-amber-400">
                        ₹{(student.due || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="p-4">
                        {isFullyPaid ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                            Fully Paid
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-amber-500/20 text-amber-300 border border-amber-500/30">
                            Due Pending
                          </span>
                        )}
                      </td>
                      <td className="p-4 text-right space-x-2">
                        {/* Download PDF Receipt */}
                        <button
                          onClick={() => generatePDFReceipt(student)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-semibold shadow-xs transition-all"
                          title="Download PDF Fee Receipt"
                        >
                          <Download className="w-3 h-3" />
                          PDF
                        </button>

                        {/* WhatsApp Reminder if due */}
                        {!isFullyPaid && (
                          <a
                            href={`https://wa.me/${student.phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hi ${student.name}, gentle reminder from CloudBinary Accounts regarding your pending fee installment of Rs. ${(student.due || 0).toLocaleString('en-IN')} for ${student.courseInterest}. Please reach out if you need payment options.`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-950/80 hover:bg-emerald-900 text-emerald-400 border border-emerald-800/60 rounded-lg text-xs font-semibold transition-all"
                            title="Send WhatsApp Payment Reminder"
                          >
                            <MessageCircle className="w-3 h-3" />
                            Reminder
                          </a>
                        )}

                        {/* Edit Fee Modal */}
                        <button
                          onClick={() => {
                            setSelectedStudent(student);
                            setFeeForm({
                              fee: student.fee || 0,
                              paid1: student.paid1 || 0,
                              paid2: student.paid2 || 0,
                              paymentMode: 'UPI / GPay / PhonePe',
                              transactionRef: '',
                            });
                            setShowUpdateModal(true);
                          }}
                          className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-semibold transition-colors"
                        >
                          Record Payment
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* RECORD PAYMENT MODAL */}
      {showUpdateModal && selectedStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-lg p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div>
                <h3 className="text-base font-bold text-white font-heading">Record Fee Payment</h3>
                <p className="text-xs text-slate-400">{selectedStudent.name} • {selectedStudent.phone}</p>
              </div>
              <button onClick={() => setShowUpdateModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUpdateFee} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Total Agreed Course Fee (INR) *</label>
                <input
                  type="number"
                  required
                  value={feeForm.fee}
                  onChange={(e) => setFeeForm({ ...feeForm, fee: Number(e.target.value) })}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 font-mono focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Installment 1 (INR)</label>
                  <input
                    type="number"
                    value={feeForm.paid1}
                    onChange={(e) => setFeeForm({ ...feeForm, paid1: Number(e.target.value) })}
                    className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 font-mono focus:outline-none focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Installment 2 (INR)</label>
                  <input
                    type="number"
                    value={feeForm.paid2}
                    onChange={(e) => setFeeForm({ ...feeForm, paid2: Number(e.target.value) })}
                    className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 font-mono focus:outline-none focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl space-y-1 text-xs">
                <div className="flex justify-between text-slate-300">
                  <span>Total Paid:</span>
                  <span className="font-bold text-emerald-400 font-mono">₹{(feeForm.paid1 + feeForm.paid2).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Calculated Balance Due:</span>
                  <span className="font-bold text-amber-400 font-mono">
                    ₹{Math.max(0, feeForm.fee - (feeForm.paid1 + feeForm.paid2)).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowUpdateModal(false)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-md shadow-indigo-600/30"
                >
                  {actionLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Save Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
